import {
  Instagram,
  Twitter,
  Linkedin,
  Facebook,
  Mail,
  MapPin,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Footer = (): JSX.Element => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-woost-black text-white py-16">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div>
            <div className="mb-4">
              <img
                src="/lovable-uploads/a640d2a4-f462-47db-aaa6-331b0e57fec0.png"
                alt="Woost"
                className="h-10"
              />
            </div>
            <p className="text-gray-300 mb-6">
              India's #1 ROI-Driven Influencer Marketing Platform connecting
              brands with 25,000+ nano & micro creators.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/woostofficial/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-woost-yellow transition-colors"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://x.com/woostinternet"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-woost-yellow transition-colors"
              >
                <Twitter size={20} />
              </a>
              <a
                href="https://www.facebook.com/WoostInternet/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-woost-yellow transition-colors"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/woost-internet/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-woost-yellow transition-colors"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/#services"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center"
                >
                  <span>Our Services</span>
                </a>
              </li>
              <li>
                <a
                  href="/#case-studies"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center"
                >
                  <span>Case Studies</span>
                </a>
              </li>
              <li>
                <a
                  href="/#content-converts"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center"
                >
                  <span>Why Creators</span>
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center"
                >
                  <span>Blog</span>
                </a>
              </li>
              <li>
                <a
                  href="/#contact"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center"
                >
                  <span>Contact Us</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/terms"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center gap-1"
                >
                  <span>Terms & Conditions</span>
                  <ExternalLink size={14} />
                </a>
              </li>
              <li>
                <a
                  href="/privacy"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center gap-1"
                >
                  <span>Privacy Policy</span>
                  <ExternalLink size={14} />
                </a>
              </li>
              <li>
                <a
                  href="/cookies"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center gap-1"
                >
                  <span>Cookie Policy</span>
                  <ExternalLink size={14} />
                </a>
              </li>
              <li>
                <a
                  href="/data-protection"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center gap-1"
                >
                  <span>Data Protection</span>
                  <ExternalLink size={14} />
                </a>
              </li>
              <li>
                <a
                  href="/child-safety.html"
                  className="text-gray-300 hover:text-woost-yellow transition-colors flex items-center gap-1"
                >
                  <span>Child Safety</span>
                  <ExternalLink size={14} />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin
                  size={20}
                  className="text-woost-yellow flex-shrink-0 mt-1"
                />
                <span className="text-gray-300">
                  H-102, Third floor, Sector 63 Rd, H Block, Sector 63, Noida,
                  Uttar Pradesh 201301
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="text-woost-yellow flex-shrink-0" />
                <a
                  href="mailto:partners@woost.io"
                  className="text-gray-300 hover:text-woost-yellow transition-colors"
                >
                  partners@woost.io
                </a>
              </li>
            </ul>

            <div className="mt-6">
              <Button
                className="bg-woost-yellow text-woost-black hover:bg-woost-yellow-light"
                onClick={() => (window.location.href = "#contact")}
              >
                Get in Touch
              </Button>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-center items-center">
          <div className="text-gray-400 mb-4 md:mb-0">
            &copy; {year} Woost. All rights reserved.
          </div>

          {/* <div className="flex gap-4">
            <a 
              href="https://play.google.com/store/apps/details?id=com.woost" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-black text-white px-6 py-3 rounded-xl h-16 flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
            >
              <img 
                src="/images/playstore.jpg" 
                alt="Get it on Google Play" 
                className="h-full w-auto"
              />
            </a>
            
            <a 
              href="https://apps.apple.com/in/app/woost-influencer-marketing/id6461419665" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-black text-white px-6 py-3 rounded-xl h-16 flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
            >
              <img 
                src="/images/appstore.png" 
                alt="Download on the App Store" 
                className="h-full w-auto"
              />
            </a>
          </div> */}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
